import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Mokshiq';
const SITE_URL = 'https://mokshiq.in';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/mokshiq-og-cover.jpg`;
const TWITTER_HANDLE = '@mokshiq_in';

/**
 * Drop this once per page (or once centrally, driven by route config)
 * to control <title>, meta description, canonical URL, Open Graph,
 * Twitter Card and JSON-LD structured data for that page.
 *
 * NOTE: This app is client-side rendered (CRA, no SSR/prerendering).
 * react-helmet-async updates the document <head> correctly for browsers
 * and for crawlers that execute JavaScript (Googlebot does this today).
 * Crawlers that DON'T execute JS (Facebook/LinkedIn/WhatsApp link
 * previews, some older bots) will only ever see the static tags baked
 * into public/index.html. If rich social previews per-page matter,
 * the long-term fix is prerendering (react-snap / Next.js), not this
 * component alone — see the README note shipped alongside this file.
 */
function Seo({
  title,
  description,
  path = '/',
  keywords,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
}) {
  const canonical = `${SITE_URL}${path}`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export default Seo;
export { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE };